import orderBy from 'lodash.orderby'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { medicareApiClient } from 'clients/medicareApiClient'
import {
  ClaimDeductiblesResponse,
  ClaimDetailsRequest,
  ClaimDetailsResponse,
  ClaimsSearchRequest,
  ClaimsSearchResponse,
  ClaimSummary,
  GetPreferenceResponse
} from '@cms-max/medicare-api-sdk'
import i18nInstance from 'i18n/config'
import {
  ClaimsFiltersForm,
  ClaimSortType,
  defaultClaimsFiltersForm
} from 'models/claims'
import { logAriaMessage } from 'redux/reducers/aria-log'

export enum Status {
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
  FINISHED = 'finished'
}

const SEARCH_RESULTS_PAGE_SIZE = 10

interface IClaimsReducer {
  filters: ClaimsFiltersForm
  searchedFilters: ClaimsFiltersForm
  response?: ClaimsSearchResponse
  results: ClaimSummary[]
  detailsResults: ClaimDetailsResponse
  deductiblesResults: ClaimDeductiblesResponse
  eMsnPreferences?: GetPreferenceResponse
  searchResultsPage: number
  sortType: ClaimSortType
  status: Status
}

interface IClaimsSelectorState {
  claims: IClaimsReducer
}

export const getFilters = (state: IClaimsSelectorState) => state.claims.filters
export const getSearchedFilters = (state: IClaimsSelectorState) =>
  state.claims.searchedFilters
export const getStatus = (state: IClaimsSelectorState) => state.claims.status
export const getAllSearchResults = (state: IClaimsSelectorState) =>
  state.claims.results
export const getSearchResults = (state: IClaimsSelectorState) => {
  const results = getAllSearchResults(state)
  const end = state.claims.searchResultsPage * SEARCH_RESULTS_PAGE_SIZE
  return results.slice(0, end)
}
export const getSearchResultsTotal = (state: IClaimsSelectorState) =>
  getAllSearchResults(state).length
export const getSortType = (state: IClaimsSelectorState) =>
  state.claims.sortType
export const getSelectedClaimDetails = (state: IClaimsSelectorState) =>
  state.claims.detailsResults
export const getEMsnPreferences = (state: IClaimsSelectorState) =>
  state.claims.eMsnPreferences
export const getClaimDeductiblesList = (state: IClaimsSelectorState) =>
  state.claims.deductiblesResults

const initialState: IClaimsReducer = {
  filters: defaultClaimsFiltersForm,
  searchedFilters: defaultClaimsFiltersForm,
  results: [],
  detailsResults: {},
  deductiblesResults: { deductibleList: [] },
  eMsnPreferences: undefined,
  searchResultsPage: 1,
  sortType: ClaimSortType.SERVICE_DATE_DESC,
  status: Status.IDLE
}

type ISortClaimsAction = {
  sortType: ClaimSortType
}

// Parse a display-formatted currency amount into float value for sortablity.
function parseClaimAmount ({ medicareApprovedCharges }: ClaimSummary): number {
  return parseFloat(medicareApprovedCharges.replace(/\$|,/g, ''))
}

// Sort a list of claims by the given sort type.
function doSortClaims (
  claims: ClaimSummary[],
  sortType: ClaimSortType
): ClaimSummary[] {
  switch (sortType) {
    case ClaimSortType.CLAIM_TYPE_ASC:
      return orderBy(claims, (c) => c.claimType, 'asc')
    case ClaimSortType.CLAIM_TYPE_DESC:
      return orderBy(claims, (c) => c.claimType, 'desc')
    case ClaimSortType.MEDICARE_APPROVED_AMOUNT_ASC:
      return orderBy(claims, parseClaimAmount, 'asc')
    case ClaimSortType.MEDICARE_APPROVED_AMOUNT_DESC:
      return orderBy(claims, parseClaimAmount, 'desc')
    case ClaimSortType.SERVICE_DATE_ASC:
      return orderBy(claims, (c) => new Date(c.startDate), 'asc')
    case ClaimSortType.SERVICE_DATE_DESC:
      return orderBy(claims, (c) => new Date(c.startDate), 'desc')
    default:
      return claims
  }
}

export const searchClaims = createAsyncThunk(
  'claims/searchClaims',
  async (request: ClaimsSearchRequest, { dispatch }) => {
    const response = await medicareApiClient.asyncSearchClaims(request)
    if (response) {
      dispatch(
        logAriaMessage(i18nInstance.t('searchComplete', { ns: 'claimsSearch' }))
      )
      return response
    } else {
      dispatch(
        logAriaMessage(
          i18nInstance.t('searchResultsUnavailable', { ns: 'claimsSearch' })
        )
      )
      throw new Error('Error fetching claims results')
    }
  }
)

export const getClaimsDetails = createAsyncThunk(
  'claims/getClaimsDetails',
  async (request: ClaimDetailsRequest, { dispatch }) => {
    const response = await medicareApiClient.asyncGetClaimsDetails(request)
    if (response) {
      dispatch(
        logAriaMessage(
          i18nInstance.t('claimsDetailsFound', { ns: 'claimsSearch' })
        )
      )
      return response
    } else {
      dispatch(
        logAriaMessage(
          i18nInstance.t('claimsDetailsUnavailable', { ns: 'claimsSearch' })
        )
      )
      throw new Error('Error fetching claims results')
    }
  }
)

export const getClaimsDeductibles = createAsyncThunk(
  'claims/getClaimsDeductibles',
  async (_, { dispatch }) => {
    const response = await medicareApiClient.asyncGetClaimsDeductibles()
    if (response) {
      dispatch(
        logAriaMessage(
          i18nInstance.t('claimsDeductiblesFound', { ns: 'claimsSearch' })
        )
      )
      return response
    } else {
      dispatch(
        logAriaMessage(
          i18nInstance.t('claimsDeductiblesUnavailable', { ns: 'claimsSearch' })
        )
      )
      throw new Error('Error fetching claim deductibles')
    }
  }
)

export const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ClaimsFiltersForm>) => {
      state.filters = action.payload
    },
    setClaimsEmsnPreferences: (
      state,
      action: PayloadAction<GetPreferenceResponse>
    ) => {
      state.eMsnPreferences = action.payload
    },
    resetFilters: (state) => {
      state.filters = defaultClaimsFiltersForm
    },
    resetDateRange: (state) => {
      state.filters.customDateRange = undefined
    },
    showMoreSearchResults: (state: IClaimsReducer) => {
      state.searchResultsPage += 1
    },
    showAllSearchResults: (state: IClaimsReducer) => {
      const total = getSearchResultsTotal({ claims: state })
      state.searchResultsPage = Math.ceil(total / SEARCH_RESULTS_PAGE_SIZE)
    },
    sortClaims: (
      state: IClaimsReducer,
      action: PayloadAction<ISortClaimsAction>
    ) => {
      const { sortType } = action.payload
      state.sortType = sortType
      state.results = doSortClaims(state.results, sortType)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchClaims.pending, (state: IClaimsReducer) => {
        state.status = Status.LOADING
        state.searchedFilters = state.filters
      })
      .addCase(searchClaims.fulfilled, (state: IClaimsReducer, { payload }) => {
        state.status = Status.FINISHED
        state.results = doSortClaims(payload.claimSummaries, state.sortType)
        state.searchResultsPage = 1
      })
      .addCase(searchClaims.rejected, (state: IClaimsReducer) => {
        state.status = Status.ERROR
      })
      .addCase(getClaimsDetails.pending, (state: IClaimsReducer) => {
        state.status = Status.LOADING
      })
      .addCase(
        getClaimsDetails.fulfilled,
        (state: IClaimsReducer, { payload }) => {
          state.status = Status.FINISHED
          state.detailsResults = payload
        }
      )
      .addCase(getClaimsDetails.rejected, (state: IClaimsReducer) => {
        state.status = Status.ERROR
      })
      .addCase(getClaimsDeductibles.pending, (state: IClaimsReducer) => {
        state.status = Status.LOADING
      })
      .addCase(
        getClaimsDeductibles.fulfilled,
        (state: IClaimsReducer, { payload }) => {
          state.status = Status.FINISHED
          state.deductiblesResults = payload
        }
      )
      .addCase(getClaimsDeductibles.rejected, (state: IClaimsReducer) => {
        state.status = Status.ERROR
      })
  }
})

export const {
  resetFilters,
  resetDateRange,
  setFilters,
  setClaimsEmsnPreferences,
  showMoreSearchResults,
  showAllSearchResults,
  sortClaims
} = claimsSlice.actions

export default claimsSlice.reducer
